import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  Calendar,
  FolderOpen,
  History,
  CheckCircle2,
} from "lucide-react";
import { getArtifact, listArtifactVersions } from "@/api/artifacts";
import type { HtmlArtifactVersionEntry } from "@/api/artifacts";
import { queryKeys } from "@/lib/queryKeys";
import { formatDate, formatRelativeTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function ArtifactDetailPage() {
  const { t } = useTranslation();
  const { artifactId, orgSlug, projectSlug } = useParams<{
    artifactId: string;
    orgSlug: string;
    projectSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => !!(location.state as Record<string, unknown> | null)?.sidebarOpen,
  );

  const prefetchVersion = (id: string) => {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.artifacts.detail(id),
      queryFn: () => getArtifact(id),
      staleTime: 30_000,
    });
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(artifact?.html_content ?? "").then(() => {
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 1500);
    });
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(
        () => setCopiedId((prev) => (prev === id ? null : prev)),
        1500,
      );
    });
  };

  const backUrl =
    orgSlug && projectSlug
      ? `/${orgSlug}/projects/${projectSlug}/artifacts`
      : null;

  const { data: artifact, isLoading, error } = useQuery({
    queryKey: queryKeys.artifacts.detail(artifactId ?? ""),
    queryFn: () => getArtifact(artifactId!),
    enabled: !!artifactId,
  });

  const { data: versionsData } = useQuery({
    queryKey: queryKeys.artifacts.versions(artifactId ?? ""),
    queryFn: () => listArtifactVersions(artifactId!),
    enabled: !!artifactId,
  });

  const versions = versionsData?.versions ?? [];
  const hasVersions = versions.length > 1;
  const currentVersion = versionsData?.versions.find(
    (v) => v.id === artifactId,
  )?.version;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !artifact) {
    return (
      <div className="space-y-4 p-6">
        <button
          onClick={() => backUrl ? navigate(backUrl) : navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t("artifactDetail.backToList")}
        </button>
        <p className="text-sm text-destructive">
          {error ? t("artifactDetail.loadError") : t("artifactDetail.notFound")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <button
        onClick={() => backUrl ? navigate(backUrl) : navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        {t("artifactDetail.backToList")}
      </button>

      {/* Header card */}
      <div className="rounded-lg border bg-card px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="capitalize">
                {artifact.artifact_type}
              </Badge>
              {currentVersion !== undefined && (
                <span className="text-xs text-muted-foreground">
                  v{currentVersion}
                </span>
              )}
            </div>
            <h2 className="text-xl font-semibold leading-tight">
              {artifact.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              {artifact.project_id && (
                <span className="flex items-center gap-1">
                  <FolderOpen className="h-3.5 w-3.5" />
                  {artifact.project_id}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(artifact.created_at)}
              </span>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyPrompt}
            >
              {copiedPrompt ? (
                <Check className="mr-1 h-4 w-4 text-green-500" />
              ) : (
                <Copy className="mr-1 h-4 w-4" />
              )}
              {copiedPrompt
                ? t("artifactDetail.copyPromptDone")
                : t("artifactDetail.copyPrompt")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                window.open(artifact.viewer_url, "_blank", "noopener,noreferrer");
              }}
            >
              <ExternalLink className="mr-1 h-4 w-4" /> {t("artifactDetail.openStandalone")}
            </Button>
          </div>
        </div>
      </div>

      {/* Content + sidebar */}
      <div className="flex gap-4">
        <iframe
          title={artifact.title}
          // Artifact scripts may run for interactive reports, but must not regain
          // the raise-admin origin or access parent application capabilities.
          sandbox="allow-scripts"
          srcDoc={artifact.html_content}
          className="h-[calc(100vh-14rem)] min-w-0 flex-1 rounded-lg border"
        />

        {hasVersions && (
          <aside
            className={cn(
              "relative overflow-hidden transition-all duration-300 ease-in-out shrink-0 rounded-lg border bg-card h-[calc(100vh-14rem)] flex flex-col cursor-pointer",
              sidebarExpanded ? "w-52" : "w-10",
            )}
            onMouseEnter={() => setSidebarExpanded(true)}
            onMouseLeave={() => setSidebarExpanded(false)}
          >
            {/* Icono cuando está colapsado — desaparece al expandir */}
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center transition-opacity duration-100 pointer-events-none",
                sidebarExpanded ? "opacity-0" : "opacity-100",
              )}
            >
              <History className="h-4 w-4 text-muted-foreground" />
            </div>

            {/* Contenido completo — aparece con fade cuando expande */}
            <div
              className={cn(
                "transition-opacity duration-200 delay-100 flex flex-col h-full",
                sidebarExpanded ? "opacity-100" : "opacity-0",
              )}
            >
            <div className="flex items-center gap-2 border-b px-3 py-3 whitespace-nowrap">
              <History className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">{t("artifactDetail.versionHistory")}</span>
              <span className="ml-auto text-xs text-muted-foreground pl-2">
                {versions.length}
              </span>
            </div>
            <div className="overflow-y-auto flex-1 p-2 space-y-1 w-52">
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((v: HtmlArtifactVersionEntry) => {
                    const isCurrent = v.id === artifactId;
                    const isRevoked = v.revoked_at !== null;
                    return (
                      <div
                        key={v.id}
                        className={cn(
                          "group relative w-full rounded-md px-3 py-1.5 transition-colors",
                          isCurrent
                            ? "bg-accent"
                            : isRevoked
                              ? "opacity-50 hover:bg-muted cursor-pointer"
                              : "hover:bg-muted cursor-pointer",
                        )}
                        onMouseEnter={
                          isCurrent ? undefined : () => prefetchVersion(v.id)
                        }
                        onClick={
                          isCurrent
                            ? undefined
                            : () => {
                                const parts =
                                  window.location.pathname.split("/");
                                parts[parts.length - 1] = v.id;
                                navigate(parts.join("/"), {
                                  state: { sidebarOpen: true },
                                });
                              }
                        }
                        role={isCurrent ? undefined : "button"}
                        tabIndex={isCurrent ? undefined : 0}
                        onKeyDown={
                          isCurrent
                            ? undefined
                            : (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  const parts =
                                    window.location.pathname.split("/");
                                  parts[parts.length - 1] = v.id;
                                  navigate(parts.join("/"), {
                                    state: { sidebarOpen: true },
                                  });
                                }
                              }
                        }
                      >
                        <div className="flex items-center gap-1.5 pr-6">
                          <span
                            className={cn(
                              "text-sm font-medium shrink-0",
                              isCurrent && "text-accent-foreground",
                            )}
                          >
                            v{v.version}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {formatRelativeTime(v.created_at)}
                          </span>
                          <div className="flex items-center gap-1 ml-auto shrink-0">
                            {isCurrent && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                            )}
                            {isRevoked && (
                              <Badge
                                variant="destructive"
                                className="px-1 py-0 text-[10px]"
                              >
                                {t("artifactDetail.revoked")}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Copiar ID — aparece al hacer hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyId(v.id);
                          }}
                          title={t("artifactDetail.copyId", { id: v.id })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-accent"
                        >
                          {copiedId === v.id ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
